import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { apiClient, ensureTokenIsSet } from "../../services/apiClient";
import {
  AiSettings,
  LiftOccupancyDryRunResult,
  PresenceCameraRole,
  PresenceCameraVerdict,
} from "../../types/api-types";

const CAMERA_ROLE_LABELS: Record<PresenceCameraRole, string> = {
  gate: "Tor",
  presence: "Anwesenheit",
  both: "Tor + Anwesenheit",
};

const AiSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AiSettings>({
    liftOccupancyCheckEnabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [dryRun, setDryRun] = useState<LiftOccupancyDryRunResult | null>(null);
  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [presenceDryRunLoading, setPresenceDryRunLoading] = useState(false);
  const [presenceDryRun, setPresenceDryRun] = useState<PresenceCameraVerdict | null>(null);
  const [presenceDryRunOpen, setPresenceDryRunOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        ensureTokenIsSet();
        setLoading(true);
        setSettings(await apiClient.getAiSettings());
        setError(null);
      } catch (err: any) {
        setError(err.message || "KI-Einstellungen konnten nicht geladen werden");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleLiftToggle = async (enabled: boolean) => {
    const previous = settings;
    const nextSettings = { ...settings, liftOccupancyCheckEnabled: enabled };
    setSettings(nextSettings);
    try {
      ensureTokenIsSet();
      setSaving(true);
      setSettings(await apiClient.updateAiSettings(nextSettings));
      setSuccess("KI-Einstellung gespeichert");
      setError(null);
    } catch (err: any) {
      setSettings(previous);
      setError(err.message || "KI-Einstellungen konnten nicht gespeichert werden");
      setSuccess(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDryRun = async (applyInternalBlockers = false) => {
    try {
      ensureTokenIsSet();
      setDryRunLoading(true);
      const result = await apiClient.runLiftOccupancyDryRun({ applyInternalBlockers });
      setDryRun(result);
      setDryRunOpen(true);
      setError(null);
      setSuccess(
        applyInternalBlockers
          ? `${result.appliedInternalBlocks ?? 0} Hebebühne(n) wurden intern belegt.`
          : null
      );
    } catch (err: any) {
      setError(err.message || "Hebebühnen-Dry-Run fehlgeschlagen");
    } finally {
      setDryRunLoading(false);
    }
  };

  const handlePresenceDryRun = async () => {
    try {
      ensureTokenIsSet();
      setPresenceDryRunLoading(true);
      setPresenceDryRun(await apiClient.testPresenceCameraConfig());
      setPresenceDryRunOpen(true);
      setError(null);
      setSuccess(null);
    } catch (err: any) {
      setError(err.message || "Anwesenheits-Dry-Run fehlgeschlagen");
    } finally {
      setPresenceDryRunLoading(false);
    }
  };

  const presenceSeverity = presenceDryRun?.ok
    ? presenceDryRun.someoneHere ? "success" : "info"
    : "warning";

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SmartToyIcon color="primary" />
          <Box>
            <Typography variant="h5" component="h1" fontWeight={700}>
              KI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Automatische KI-Prüfungen für Chat und WhatsApp
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Anwesenheits-Erkennung
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Prüft die gespeicherten Kameras auf offenes Tor und sichtbare Personen. Die Kamera-Zugänge bleiben unter System konfiguriert.
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrowIcon />}
                    onClick={handlePresenceDryRun}
                    disabled={loading || presenceDryRunLoading}
                  >
                    {presenceDryRunLoading ? "Prüfe..." : "Dry-Run"}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Hebebühnen-Bildprüfung
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Wenn rechnerisch eine Hebebühne frei ist, prüft der Chat die markierten Kamerabereiche auf ein Fahrzeug und eskaliert bei Verdacht an Mitarbeiter.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.liftOccupancyCheckEnabled}
                      disabled={loading || saving}
                      onChange={(event) => handleLiftToggle(event.target.checked)}
                    />
                  }
                  label={saving ? "Speichert..." : "Aktiv"}
                />
                <Button
                  variant="outlined"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleDryRun(false)}
                  disabled={loading || dryRunLoading}
                >
                  {dryRunLoading ? "Prüfe..." : "Dry-Run"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleDryRun(true)}
                  disabled={loading || dryRunLoading}
                >
                  {dryRunLoading ? "Prüfe..." : "Dry-Run + intern belegen"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
      </Stack>

      <Dialog open={presenceDryRunOpen} onClose={() => setPresenceDryRunOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Anwesenheits-Dry-Run Ergebnis</DialogTitle>
        <DialogContent dividers>
          {presenceDryRun && (
            <Stack spacing={2}>
              <Alert severity={presenceSeverity}>
                {presenceDryRun.ok
                  ? `Urteil: ${presenceDryRun.someoneHere ? "jemand ist da" : "niemand sichtbar"} - Tor offen: ${presenceDryRun.doorOpen ? "ja" : "nein"}, Person sichtbar: ${presenceDryRun.personPresent ? "ja" : "nein"}`
                  : `Kein verwertbares Kamerasignal (${presenceDryRun.error ?? "unbekannter Fehler"}).`}
              </Alert>
              {presenceDryRun.beschreibung && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {presenceDryRun.beschreibung}
                </Typography>
              )}
              {presenceDryRun.cameras && presenceDryRun.cameras.length > 0 && (
                <Stack spacing={1}>
                  {presenceDryRun.cameras.map((cam) => (
                    <Box key={cam.id} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2">{cam.label || cam.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {CAMERA_ROLE_LABELS[cam.role]}
                          </Typography>
                        </Box>
                        {cam.ok ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(cam.role === "gate" || cam.role === "both") && (
                              <Chip size="small" variant="outlined" color={cam.tor_offen ? "success" : "default"} label={cam.tor_offen ? "Tor offen" : "Tor zu"} />
                            )}
                            {(cam.role === "presence" || cam.role === "both") && (
                              <Chip size="small" variant="outlined" color={cam.person_sichtbar ? "success" : "default"} label={cam.person_sichtbar ? "Person" : "keine Person"} />
                            )}
                          </Stack>
                        ) : (
                          <Chip size="small" color="warning" variant="outlined" label={cam.error ?? "Fehler"} />
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresenceDryRunOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dryRunOpen} onClose={() => setDryRunOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Hebebühnen-Dry-Run Ergebnis</DialogTitle>
        <DialogContent dividers>
          {dryRun && (
            <Stack spacing={1.5}>
              <Alert severity={dryRun.lifts.some((lift) => lift.wouldEscalate) ? "warning" : "info"}>
                {dryRun.summary}
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Bildprüfung: ${dryRun.enabled ? "aktiv" : "deaktiviert"}`} />
                <Chip size="small" label={`Kameras: ${dryRun.cameraConfigured ? "konfiguriert" : "nicht aktiv"}`} />
                <Chip size="small" label={`Chat-Pfad: ${dryRun.wouldRunInChat ? "würde laufen" : "würde überspringen"}`} />
                {dryRun.availability && (
                  <Chip
                    size="small"
                    label={`Rechnerisch frei: ${dryRun.availability.free} von ${dryRun.availability.total}`}
                  />
                )}
                {dryRun.appliedInternalBlocks !== undefined && (
                  <Chip size="small" color="warning" label={`Intern belegt: ${dryRun.appliedInternalBlocks}`} />
                )}
              </Stack>
              {dryRun.lifts.length > 0 ? (
                <Stack spacing={1}>
                  {dryRun.lifts.map((lift) => (
                    <Box
                      key={lift.lift}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2">Hebebühne {lift.lift}</Typography>
                          <Typography variant="body2" color="text.secondary">{lift.message}</Typography>
                          {lift.checkedCameras.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Kameras: {lift.checkedCameras.join(", ")}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            color={lift.wouldEscalate ? "warning" : lift.status === "occupied" ? "info" : lift.chatStatus === "answered" ? "success" : "default"}
                            label={
                              lift.chatStatus === "pending_human"
                                ? "Eskalation"
                                : lift.status === "occupied"
                                  ? "Belegt"
                                  : lift.chatStatus === "answered"
                                    ? "Antwort"
                                    : "Übersprungen"
                            }
                          />
                          {lift.confidence !== undefined && (
                            <Chip size="small" variant="outlined" label={`Confidence ${Math.round(lift.confidence * 100)}%`} />
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Keine Hebebühnen-Zonen konfiguriert.
                </Typography>
              )}
              {dryRun.error && <Alert severity="warning">{dryRun.error}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDryRunOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AiSettingsPage;
